import { compatApi } from './Intercepter';

/**
 * 이미지 파일 확장자 목록
 */
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];

/**
 * 파일이 이미지인지 확인
 * @param {string} fileName - 파일명
 * @returns {boolean} 이미지 여부
 */
export const isImageFile = (fileName) => {
  if (!fileName) return false;
  const ext = fileName.toLowerCase().split('.').pop();
  return IMAGE_EXTENSIONS.includes(ext);
};

/**
 * 토큰 헤더가 포함된 이미지를 Blob으로 가져와서 Object URL 생성
 * @param {string} fileId - 파일 ID
 * @param {string} apiPath - API 경로 (기본값: '/file/download/project')
 * @returns {Promise<string|null>} Object URL 또는 null
 */
export const loadImageWithAuth = async (fileId, apiPath = '/file/download/project') => {
  try {
    const response = await compatApi.get(`${apiPath}/${fileId}`, {
      responseType: 'blob'
    });

    if (response.ok && response.data) {
      const blob = response.data instanceof Blob 
        ? response.data 
        : new Blob([response.data]);
      return window.URL.createObjectURL(blob);
    }
    return null;
  } catch (error) {
    console.error(`이미지 로드 실패 (fileId: ${fileId}):`, error);
    return null;
  }
};

/**
 * Notification 이미지를 push ID와 이미지 이름으로 로드하여 Object URL 생성
 * @param {string|number} pushId - Push ID
 * @param {string} imageName - 이미지 파일명
 * @returns {Promise<string|null>} Object URL 또는 null
 */
export const loadNotificationImage = async (pushId, imageName) => {
  try {
    const response = await compatApi.get(`/file/download/notification/${pushId}/${imageName}`, {
      responseType: 'blob'
    });

    if (response.ok && response.data) {
      const blob = response.data instanceof Blob 
        ? response.data 
        : new Blob([response.data]);
      return window.URL.createObjectURL(blob);
    }
    return null;
  } catch (error) {
    console.error(`Notification 이미지 로드 실패 (pushId: ${pushId}, imageName: ${imageName}):`, error);
    return null;
  }
};

/**
 * 파일 목록에서 이미지들의 미리보기 URL을 일괄 생성
 * @param {Array} files - 파일 객체 배열
 * @param {string} apiPath - API 경로 (기본값: '/file/download/project')
 * @returns {Promise<Object>} fileId를 key로 하는 URL 객체
 */
export const loadMultipleImagePreviews = async (files, apiPath = '/file/download/project') => {
  const previewUrls = {};
  
  const imageLoadPromises = files
    .filter(fileObj => fileObj.id && isImageFile(fileObj.fileName))
    .map(async (fileObj) => {
      const url = await loadImageWithAuth(fileObj.id, apiPath);
      if (url) {
        previewUrls[fileObj.id] = url;
      }
    });

  await Promise.all(imageLoadPromises);
  return previewUrls;
};

/**
 * 로컬 파일(File 객체)의 미리보기 URL 생성
 * @param {File} file - 로컬 파일 객체
 * @returns {string|null} Object URL 또는 null
 */
export const createLocalImagePreview = (file) => {
  if (!file || !isImageFile(file.name)) return null;
  return URL.createObjectURL(file);
};

/**
 * 여러 로컬 파일의 미리보기 URL 생성
 * @param {Array<File>} files - 로컬 파일 배열
 * @returns {Object} 임시 key를 가진 URL 객체
 */
export const createMultipleLocalPreviews = (files) => {
  const previewUrls = {};
  const timestamp = Date.now();
  
  files.forEach((file, index) => {
    if (isImageFile(file.name)) {
      const url = URL.createObjectURL(file);
      previewUrls[`temp_${timestamp}_${index}`] = url;
    }
  });
  
  return previewUrls;
};

/**
 * Object URL 해제
 * @param {string} url - Object URL
 */
export const revokeImageUrl = (url) => {
  if (url && url.startsWith('blob:')) {
    window.URL.revokeObjectURL(url);
  }
};

/**
 * 여러 Object URL 일괄 해제
 * @param {Object|Array} urls - URL 객체 또는 배열
 */
export const revokeMultipleImageUrls = (urls) => {
  if (Array.isArray(urls)) {
    urls.forEach(revokeImageUrl);
  } else if (typeof urls === 'object') {
    Object.values(urls).forEach(revokeImageUrl);
  }
};

/**
 * 파일 다운로드
 * @param {string} fileId - 파일 ID
 * @param {string} fileName - 저장될 파일명
 * @param {string} apiPath - API 경로 (기본값: '/file/download/project')
 * @param {Object} params - 쿼리 파라미터로 함께 보낼 값 (예: 조직 매니저의 타 조직원 파일 다운로드 시 userEmail/organizationCode)
 * @returns {Promise<boolean>} 성공 여부
 */
export const downloadFile = async (fileId, fileName, apiPath = '/file/download/project', params = {}) => {
  try {
    const response = await compatApi.get(`${apiPath}/${fileId}`, {
      responseType: 'blob',
      params
    });

    if (response.ok && response.data) {
      const blob = response.data instanceof Blob 
        ? response.data 
        : new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    }
    return false;
  } catch (error) {
    console.error('파일 다운로드 오류:', error);
    return false;
  }
};

/**
 * React 컴포넌트용 이미지 미리보기 관리 훅 (복사해서 사용)
 * 
 * 사용 예시:
 * const { previewUrls, loadPreviews, addLocalPreviews, removePreview, cleanupAll } = useImagePreviews();
 * 
 * useEffect(() => {
 *   if (serverFiles.length > 0) {
 *     loadPreviews(serverFiles);
 *   }
 *   return cleanupAll;
 * }, [serverFiles]);
 */
export const useImagePreviewsExample = `
import { useState, useCallback } from 'react';
import { loadMultipleImagePreviews, createMultipleLocalPreviews, revokeImageUrl, revokeMultipleImageUrls } from '../utils/ImageUtil';

export const useImagePreviews = () => {
  const [previewUrls, setPreviewUrls] = useState({});

  const loadPreviews = useCallback(async (files, apiPath) => {
    const urls = await loadMultipleImagePreviews(files, apiPath);
    setPreviewUrls(prev => ({ ...prev, ...urls }));
    return urls;
  }, []);

  const addLocalPreviews = useCallback((files) => {
    const urls = createMultipleLocalPreviews(files);
    setPreviewUrls(prev => ({ ...prev, ...urls }));
    return urls;
  }, []);

  const removePreview = useCallback((key) => {
    setPreviewUrls(prev => {
      if (prev[key]) {
        revokeImageUrl(prev[key]);
        const newUrls = { ...prev };
        delete newUrls[key];
        return newUrls;
      }
      return prev;
    });
  }, []);

  const cleanupAll = useCallback(() => {
    revokeMultipleImageUrls(previewUrls);
    setPreviewUrls({});
  }, [previewUrls]);

  return { previewUrls, loadPreviews, addLocalPreviews, removePreview, cleanupAll };
};
`;
